import React, { memo, useMemo } from "react";
import type { CommercialConfig } from "./commercialConfig";
import {
  formatDemoDocumentNumber,
  getDevise,
  getDisplayAddress,
  getDisplayCompanyName,
  getLogoUrl,
  getPiedPageFacture,
  getSignatureImageUrl,
  getTauxTvaPercent,
} from "./commercialConfig";
import { formatMoney } from "./formatMoney";

export type InvoicePreviewProps = {
  config: CommercialConfig;
};

function InvoicePreviewInner({ config }: InvoicePreviewProps): React.JSX.Element {
  const cur = getDevise(config);
  const tauxPct = getTauxTvaPercent(config);
  const exo = config.exonereTva === true;
  const { demoHt, demoTva, demoTtc } = useMemo(() => {
    const ht = 250;
    const rate = exo ? 0 : tauxPct / 100;
    const tva = ht * rate;
    return { demoHt: ht, demoTva: tva, demoTtc: ht + tva };
  }, [exo, tauxPct]);

  const docNo = useMemo(
    () => formatDemoDocumentNumber(config, "facture"),
    [config]
  );
  const echeance = useMemo(() => {
    const d = config.delaiPaiementJours;
    if (d == null || !Number.isFinite(d) || d < 0) return "—";
    const t = new Date();
    t.setDate(t.getDate() + d);
    return t.toLocaleDateString("fr-FR");
  }, [config.delaiPaiementJours]);

  const bankLines = [
    config.banque?.trim() && `Banque : ${config.banque}`,
    config.nomCompte?.trim() && `Compte : ${config.nomCompte}`,
    config.numeroCompte?.trim() && `N° compte : ${config.numeroCompte}`,
    config.iban?.trim() && `IBAN : ${config.iban}`,
    config.swift?.trim() && `SWIFT : ${config.swift}`,
    config.deviseCompte?.trim() && `Devise compte : ${config.deviseCompte}`,
    config.mobileMoneyEntreprise?.trim() && `Mobile money : ${config.mobileMoneyEntreprise}`,
  ].filter(Boolean) as string[];

  const title = getDisplayCompanyName(config);
  const addr = getDisplayAddress(config);
  const logo = getLogoUrl(config);
  const sig = getSignatureImageUrl(config);
  const footer = getPiedPageFacture(config);

  return (
    <>
      <div className="flex justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          {logo ? (
            <img src={logo} alt="" className="mb-2 h-12 max-w-[140px] object-contain" />
          ) : (
            <div className="mb-2 h-12 w-28 rounded bg-slate-100 text-center text-[10px] leading-[48px] text-slate-400">
              Logo
            </div>
          )}
          <p className="text-sm font-bold">{title}</p>
          {config.slogan?.trim() ? <p className="text-[10px] text-slate-600">{config.slogan}</p> : null}
          <div className="mt-2 text-[10px] leading-relaxed text-slate-600">
            {addr ? <p className="whitespace-pre-line">{addr}</p> : null}
            {config.telephone?.trim() ? <p>Tél. {config.telephone}</p> : null}
            {config.email?.trim() ? <p>{config.email}</p> : null}
            {config.siteWeb?.trim() ? <p>{config.siteWeb}</p> : null}
            <p>
              RCCM : {config.rccm?.trim() || "—"} · NIF : {config.nif?.trim() || "—"} · ID NAT :{" "}
              {config.idNat?.trim() || "—"}
              {config.numeroImpotTva?.trim() ? (
                <>
                  {" "}
                  · N° impôt TVA : {config.numeroImpotTva}
                </>
              ) : null}
            </p>
          </div>
        </div>
        <div className="text-right text-[10px] text-slate-600">
          <p className="text-lg font-bold text-slate-900">FACTURE</p>
          <p className="mt-1 font-mono">{docNo}</p>
          <p>Émission : {new Date().toLocaleDateString("fr-FR")}</p>
          <p>Échéance : {echeance}</p>
        </div>
      </div>
      <div className="mt-4 text-[10px]">
        <p className="font-semibold text-slate-800">Client</p>
        <p className="text-slate-700">Client démo — Kinshasa</p>
      </div>
      <table className="mt-4 w-full border-collapse text-[10px]">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left">
            <th className="py-1.5 pr-2">Description</th>
            <th className="py-1.5 pr-2">Qté</th>
            <th className="py-1.5 pr-2">P.U. HT</th>
            <th className="py-1.5 text-right">Montant HT</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-100">
            <td className="py-2">Abonnement assainissement — mensuel</td>
            <td>1</td>
            <td>{formatMoney(demoHt, cur)}</td>
            <td className="text-right">{formatMoney(demoHt, cur)}</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-4 ml-auto w-[220px] space-y-1 text-[10px]">
        <div className="flex justify-between">
          <span className="text-slate-600">Total HT</span>
          <span>{formatMoney(demoHt, cur)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">
            TVA ({exo ? "exonéré — " : ""}
            {tauxPct}%)
          </span>
          <span>{formatMoney(demoTva, cur)}</span>
        </div>
        {exo && config.mentionTva?.trim() ? (
          <p className="text-[9px] leading-snug text-slate-500">{config.mentionTva}</p>
        ) : null}
        <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
          <span>Total TTC</span>
          <span>{formatMoney(demoTtc, cur)}</span>
        </div>
        <div className="flex justify-between text-slate-600">
          <span>Solde dû</span>
          <span>{formatMoney(demoTtc, cur)}</span>
        </div>
      </div>
      {bankLines.length > 0 ? (
        <div className="mt-6 border-t border-slate-200 pt-3 text-[10px] text-slate-700">
          <p className="font-semibold text-slate-800">Coordonnées bancaires</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {bankLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {config.conditionsPaiement?.trim() ? (
        <div className="mt-6 border-t border-slate-200 pt-3 text-[10px] text-slate-700">
          <p className="font-semibold text-slate-800">Conditions de paiement</p>
          <p className="mt-1 whitespace-pre-line">{config.conditionsPaiement}</p>
          {config.penaliteRetard?.trim() ? (
            <p className="mt-2 text-[9px] text-slate-600">Pénalités : {config.penaliteRetard}</p>
          ) : null}
        </div>
      ) : null}
      {config.clauseContestation?.trim() ? (
        <div className="mt-4 text-[9px] text-slate-600">
          <p className="font-semibold text-slate-700">Contestation</p>
          <p className="mt-1 whitespace-pre-line">{config.clauseContestation}</p>
        </div>
      ) : null}
      {footer ? (
        <div className="mt-4 whitespace-pre-line text-[9px] text-slate-500">{footer}</div>
      ) : null}
      {config.mentionsLegales?.trim() ? (
        <div className="mt-3 whitespace-pre-line text-[8px] leading-snug text-slate-400">
          {config.mentionsLegales}
        </div>
      ) : null}
      <div className="mt-8 flex justify-end gap-6">
        <div className="text-center text-[9px] text-slate-500">
          {sig ? (
            <img src={sig} alt="" className="mx-auto h-16 object-contain" />
          ) : (
            <div className="mx-auto h-16 w-32 rounded border border-dashed border-slate-200" />
          )}
          <p className="mt-1 font-medium text-slate-700">
            {config.nomSignataire?.trim() || "Signature / cachet"}
          </p>
          {config.fonctionSignataire?.trim() ? (
            <p className="text-[8px] text-slate-500">{config.fonctionSignataire}</p>
          ) : (
            <p className="text-[8px] text-slate-400">Fonction</p>
          )}
        </div>
      </div>
    </>
  );
}

export const InvoicePreview = memo(InvoicePreviewInner);
